import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router"
import {
  declarationImportPath,
  declarationReviewPath,
} from "../material-compliance/components/declarations/routes"
import { pilotRequests, type PilotRequest } from "./pilotRequestsApi"
import { ReleaseHistory } from "./ReleaseHistory"

export function PilotHandoffPanel() {
  const enabled = useFeatureFlag("COMPANY_INDEX")
  const handoff = useFeatureFlag("COMPANY_INDEX_ARTICLE_HANDOFF")
  const { hasPermission } = useUser()
  if (!enabled || !handoff || !hasPermission(PERMISSIONS.MC_DECLARATIONS_VIEW))
    return null
  return <HandoffQueue />
}
function HandoffQueue() {
  const { user } = useUser()
  const { translate: t } = useTranslation()
  const [selected, setSelected] = useState("")
  const requests = useInfiniteQuery({
    queryKey: ["company-index", user?.organization, "review-requests"],
    queryFn: ({ pageParam }) => pilotRequests.all(pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === 30 ? pages.length * 30 : undefined,
  })
  const rows = requests.data?.pages.flat() ?? []
  const request = rows.find((r) => r.id === selected)
  return (
    <details className="rounded-lg border bg-card p-4">
      <summary className="cursor-pointer font-medium">
        {t("CI_HANDOFF_TITLE")}
      </summary>
      <div className="space-y-3 mt-4">
        <p className="text-sm text-muted-foreground">{t("CI_HANDOFF_HINT")}</p>
        <label className="block">
          {t("CI_HANDOFF_REQUEST")}
          <select
            className="mt-1 w-full rounded-md border border-input bg-background p-2"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">—</option>
            {rows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title} · {r.scope.issuerDomain}
              </option>
            ))}
          </select>
        </label>
        {requests.hasNextPage && (
          <Button
            variant="outline"
            disabled={requests.isFetchingNextPage}
            onClick={() => void requests.fetchNextPage()}
          >
            {t("CI_MORE")}
          </Button>
        )}
        <Button
          variant="outline"
          disabled={requests.isFetching}
          onClick={() => void requests.refetch()}
        >
          {t("CI_REFRESH")}
        </Button>
        {requests.isError && <p role="alert">{t("CI_ERROR")}</p>}
        {request && (
          <RequestHandoff
            key={`${request.id}-${request.revision}`}
            request={request}
          />
        )}
      </div>
    </details>
  )
}
function RequestHandoff({ request }: { request: PilotRequest }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const [selected, setSelected] = useState<string[]>([])
  const [recipient, setRecipient] = useState("")
  const [publicSources, setPublicSources] = useState(false)
  const canRelease = [
    PERMISSIONS.MC_DECLARATIONS_REVIEW,
    PERMISSIONS.MC_MASTER_DATA_EDIT,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  ].every(hasPermission)
  const declarations = useInfiniteQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "request-declarations",
      request.id,
      request.revision,
    ],
    queryFn: ({ pageParam }) =>
      pilotRequests.declarations(request.id, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === 100 ? pages.length * 100 : undefined,
  })
  const declarationRows = declarations.data?.pages.flat() ?? []
  const prepare = useMutation({
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ["company-index", user?.organization, "releases", request.id],
      })
    },
    mutationFn: () =>
      pilotRequests.prepareRelease(request.id, {
        recipientOrganization: recipient.trim(),
        declarationIds: selected,
        expectedRevision: request.revision,
        publicSourcesConfirmed: true,
      }),
  })
  const download = useMutation({ mutationFn: pilotRequests.downloadRelease })
  const approvedIds = new Set(
    declarationRows
      .filter((d) => d.approvedAt && d.processingStatus === "processed")
      .map((d) => d.id),
  )
  const selectedHashes = new Set(
    declarationRows
      .filter((d) => selected.includes(d.id))
      .map((d) => d.contentSha256),
  )
  const normalize = (part: string) =>
    request.scope.issuerDomain === "harting.com"
      ? part.replace(/\s/g, "")
      : part
  const pairs = request.scope.requirements.flatMap((r) =>
    r.frameworks.map((framework) => ({ partNumber: r.partNumber, framework })),
  )
  const outstanding = pairs.filter(
    (pair) =>
      !request.matches.some(
        (m) =>
          m.decision === "supported" &&
          selectedHashes.has(m.sha256) &&
          normalize(m.partNumber) === normalize(pair.partNumber) &&
          m.framework === pair.framework,
      ),
  )
  const valid =
    publicSources &&
    recipient.trim() &&
    recipient.trim() !== user?.organization &&
    selected.length > 0 &&
    selected.every((id) => approvedIds.has(id))
  function resetRelease() {
    prepare.reset()
    download.reset()
  }
  return (
    <div className="space-y-3">
      <Link className="underline" to={declarationImportPath}>
        {t("CI_HANDOFF_UPLOAD")}
      </Link>
      <Button
        variant="outline"
        disabled={declarations.isFetching}
        onClick={() => {
          resetRelease()
          void declarations.refetch()
        }}
      >
        {t("CI_REFRESH")}
      </Button>
      {declarations.isError && <p role="alert">{t("CI_ERROR")}</p>}
      {!declarations.isLoading && declarationRows.length === 0 && (
        <p>{t("CI_HANDOFF_EMPTY")}</p>
      )}
      {declarationRows.map((d) => (
        <div
          key={d.id}
          className="flex items-center justify-between gap-3 rounded border p-3"
        >
          <label className="flex items-center gap-2">
            {canRelease && (
              <input
                type="checkbox"
                aria-label={d.fileName}
                disabled={!approvedIds.has(d.id) || prepare.isPending}
                checked={selected.includes(d.id)}
                onChange={(e) => {
                  resetRelease()
                  setSelected(
                    e.target.checked
                      ? [...selected, d.id]
                      : selected.filter((id) => id !== d.id),
                  )
                }}
              />
            )}
            <span>{d.fileName}</span>
          </label>
          <Link className="underline" to={declarationReviewPath(d.id)}>
            {t("CI_HANDOFF_REVIEW")}
          </Link>
        </div>
      ))}
      {declarations.hasNextPage && (
        <Button
          variant="outline"
          disabled={declarations.isFetchingNextPage}
          onClick={() => void declarations.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
      {canRelease && (
        <div className="rounded border p-3 text-sm">
          <p>
            {t("CI_HANDOFF_COVERAGE")}: {pairs.length - outstanding.length} /{" "}
            {pairs.length}
          </p>
          <div>
            <p>{t("CI_HANDOFF_OUTSTANDING")}</p>
            {outstanding.length ? (
              <ul>
                {outstanding.map((p, i) => (
                  <li key={i}>
                    {p.partNumber} · {p.framework.toUpperCase()}
                  </li>
                ))}
              </ul>
            ) : (
              <p>—</p>
            )}
          </div>
          <p className="text-muted-foreground">
            {t("CI_HANDOFF_COVERAGE_HINT")}
          </p>
        </div>
      )}
      {canRelease && <ReleaseHistory requestId={request.id} />}
      {canRelease && (
        <>
          <label className="block">
            {t("CI_HANDOFF_RECIPIENT")}
            <Input
              value={recipient}
              maxLength={200}
              disabled={prepare.isPending}
              onChange={(e) => {
                resetRelease()
                setRecipient(e.target.value)
              }}
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={publicSources}
              disabled={prepare.isPending}
              onChange={(e) => {
                resetRelease()
                setPublicSources(e.target.checked)
              }}
            />
            {t("CI_HANDOFF_PUBLIC")}
          </label>
          <Button
            disabled={!valid || prepare.isPending}
            onClick={() => prepare.mutate()}
          >
            {t("CI_HANDOFF_PREPARE")}
          </Button>
          {prepare.data && (
            <div className="space-y-2">
              <p>{t("CI_HANDOFF_MANUAL")}</p>
              <Button
                disabled={download.isPending}
                onClick={() => download.mutate(prepare.data.id)}
              >
                {t("CI_HANDOFF_DOWNLOAD")}
              </Button>
            </div>
          )}
          {(prepare.isError || download.isError) && (
            <p role="alert">{t("CI_HANDOFF_ERROR")}</p>
          )}
        </>
      )}
    </div>
  )
}
